<template>
  <div class="tags-input-container" @click="focusTagInput">
    <span
      v-for="(tag, index) in value"
      :key="index"
      class="tag-pill"
    >
      {{ tag }}
      <button
        type="button"
        class="tag-remove-btn"
        aria-label="Remove tag"
        @click.stop="removeTag(index)"
      >
        &times;
      </button>
    </span>
    <input
      ref="tagInput"
      :id="inputId || null"
      v-model="draft"
      type="text"
      class="tag-text-input"
      :placeholder="placeholder"
      @keydown.enter.prevent="commit"
      @keydown.,="commit"
      @keydown.delete="handleBackspace"
      @blur="commit"
    />
  </div>
</template>

<script>
export default {
  name: 'TagInput',

  // Vue 2 default model: v-model over an array of tag strings.
  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Type and press Enter or click away to add',
    },
    inputId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      // Transient text-field contents before commit. Never surfaced to the host.
      draft: '',
    };
  },

  methods: {
    // Move keyboard focus to the inline text field (container click handler).
    focusTagInput() {
      this.$refs.tagInput.focus();
    },

    // Commit the draft on Enter, comma, or blur. Splits on commas, trims,
    // drops empties, then appends each token not already present under
    // case-sensitive comparison. Emits a fresh array only when something
    // changed; always clears the draft. Never mutates this.value.
    commit() {
      const tokens = this.draft
        .split(',')
        .map((token) => token.trim())
        .filter((token) => token !== '');

      const next = [...this.value];
      tokens.forEach((token) => {
        if (!next.includes(token)) {
          next.push(token);
        }
      });

      if (next.length !== this.value.length) {
        this.$emit('input', next);
      }

      this.draft = '';
    },

    // Remove the tag at the given index, emitting a fresh array.
    removeTag(index) {
      this.$emit(
        'input',
        this.value.filter((_, i) => i !== index)
      );
    },

    // On an empty field with at least one tag, remove the last tag.
    // No-op when the draft is non-empty or the value array is empty.
    handleBackspace() {
      if (this.draft === '' && this.value.length > 0) {
        this.$emit('input', this.value.slice(0, -1));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tags-input-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  padding: 0.375rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background: #fff;
  cursor: text;
  min-height: 38px;

  &:focus-within {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

.tag-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  background-color: #e7f1ff;
  color: #0d6efd;
  border-radius: 1rem;
  font-size: 0.8rem;
  white-space: nowrap;
}

.tag-remove-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #0d6efd;
  font-size: 1rem;
  line-height: 1;
  padding: 0 0.15rem;
  cursor: pointer;
  border-radius: 50%;

  &:hover {
    background-color: #0d6efd;
    color: #fff;
  }
}

.tag-text-input {
  flex: 1 1 80px;
  border: none;
  outline: none;
  padding: 0.1rem 0;
  font-size: 0.875rem;
  min-width: 80px;
}
</style>
